import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Building2, Users, Calendar, AlertTriangle, TrendingUp, Hourglass } from 'lucide-react';
import { apiFetch } from '../lib/api';

interface TenantRow {
  id: string;
  slug: string;
  tenant_code: string;
  company_name: string;
  status: 'pending_approval' | 'trial' | 'active' | 'suspended' | 'archived';
  plan: string | null;
  trial_ends_at: string | null;
  created_at: string;
  cutoff_day: number | null;
  stats: { employees: number; users: number; last_audit_at: string | null };
}

interface StatsRow {
  total: number;
  by_status: Record<string, number>;
  trial_expiring_7d: number;
  signups_last_30d: number;
}

export function TenantList() {
  const { data: tenants, isLoading } = useQuery({
    queryKey: ['platform', 'tenants'],
    queryFn: () => apiFetch<TenantRow[]>('/platform/tenants'),
    refetchInterval: 60_000,
  });
  const { data: stats } = useQuery({
    queryKey: ['platform', 'stats'],
    queryFn: () => apiFetch<StatsRow>('/platform/stats'),
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-400">Payroll Edge</div>
            <h1 className="text-lg font-semibold">Platform Admin</h1>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* KPI strip */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <KPI icon={Hourglass} label="รออนุมัติ" value={stats.by_status.pending_approval ?? 0} accent="amber" />
            <KPI icon={Building2} label="ทั้งหมด" value={stats.total} />
            <KPI icon={TrendingUp} label="Active" value={stats.by_status.active ?? 0} accent="green" />
            <KPI icon={Calendar} label="Trial หมดอายุ 7d" value={stats.trial_expiring_7d} accent="amber" />
            <KPI icon={Users} label="Signup 30d" value={stats.signups_last_30d} accent="blue" />
          </div>
        )}

        {/* Tenant cards */}
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-gray-700">บริษัทที่สมัครแล้ว</h2>
          {isLoading ? (
            <p className="text-center text-gray-500 py-8">กำลังโหลด…</p>
          ) : !tenants || tenants.length === 0 ? (
            <p className="text-center text-gray-500 py-8">ยังไม่มี tenant ในระบบ</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
              {tenants.map((t) => <TenantCard key={t.id} t={t} />)}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function TenantCard({ t }: { t: TenantRow }) {
  const statusBadge: Record<string, string> = {
    pending_approval: 'bg-amber-100 text-amber-700 animate-pulse',
    trial: 'bg-blue-100 text-blue-700',
    active: 'bg-green-100 text-green-700',
    suspended: 'bg-orange-100 text-orange-700',
    archived: 'bg-gray-100 text-gray-500',
  };
  const statusLabel: Record<string, string> = {
    pending_approval: 'รออนุมัติ',
    trial: 'trial',
    active: 'active',
    suspended: 'suspended',
    archived: 'archived',
  };
  const trialDays =
    t.trial_ends_at
      ? Math.ceil((new Date(t.trial_ends_at).getTime() - Date.now()) / 86_400_000)
      : null;
  const trialExpiring = t.status === 'trial' && trialDays !== null && trialDays <= 7;

  return (
    <Link to={`/tenants/${t.id}`} className="card p-3 hover:shadow-md transition block">
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate">{t.company_name}</div>
          <div className="text-xs text-gray-500 mt-0.5">
            <span className="font-mono">{t.tenant_code}</span>
            {' · '}
            <span>{t.plan ?? 'starter'}</span>
          </div>
        </div>
        <span className={`badge ${statusBadge[t.status] ?? 'bg-gray-100 text-gray-500'}`}>
          {statusLabel[t.status] ?? t.status}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 text-xs text-gray-600 mt-2">
        <div>
          <div className="text-gray-400">พนักงาน</div>
          <div className="font-semibold text-gray-700">{t.stats.employees}</div>
        </div>
        <div>
          <div className="text-gray-400">Users</div>
          <div className="font-semibold text-gray-700">{t.stats.users}</div>
        </div>
        <div>
          <div className="text-gray-400">สมัครเมื่อ</div>
          <div className="font-semibold text-gray-700">
            {new Date(t.created_at).toLocaleDateString('th-TH', { day: 'numeric', month: 'short' })}
          </div>
        </div>
      </div>

      {trialExpiring && (
        <div className="mt-2 text-xs bg-amber-50 text-amber-800 rounded px-2 py-1 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> Trial เหลืออีก {trialDays} วัน
        </div>
      )}
    </Link>
  );
}

function KPI({ icon: Icon, label, value, accent }: {
  icon: typeof Building2;
  label: string;
  value: number;
  accent?: 'green' | 'amber' | 'blue';
}) {
  const accentClass =
    accent === 'green' ? 'text-green-700 bg-green-50'
    : accent === 'amber' ? 'text-amber-700 bg-amber-50'
    : accent === 'blue' ? 'text-blue-700 bg-blue-50'
    : 'text-gray-700 bg-gray-50';
  return (
    <div className="card p-3 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${accentClass}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-xs text-gray-500">{label}</div>
        <div className="text-xl font-bold">{value.toLocaleString('th-TH')}</div>
      </div>
    </div>
  );
}
